<script setup>
import { ref, computed, watch } from 'vue';
import { useUserStore } from '@/store/user-store';
import { computeValuationFromAPI } from '@/lib/valuation';
import computeFCFFValuation from '@/lib/valuation_fcff';
import { computePBValuation } from '@/lib/pbvaluation';
import StockAdditionPortfolioImpact from '@/sections/stock-analysis/StockAdditionPortfolioImpact.vue';
import StockAnalysisTechIndicators from '@/sections/stock-analysis/StockAnalysisTechIndicators.vue';
import SectorDiscoveryRail from '@/components/stock-analysis/SectorDiscoveryRail.vue';
import StockDiscoveryPanel from '@/components/stock-analysis/StockDiscoveryPanel.vue';
import AnalysisTabs from '@/components/stock-analysis/AnalysisTabs.vue';
import EmptyAnalysisState from '@/components/stock-analysis/EmptyAnalysisState.vue';
import StockOverview from '@/components/stock-analysis/StockOverview.vue';
import StockDialog from '@/components/dashboard/StockDialog.vue';

const userStore = useUserStore();

// Stock and sector data
const STOCK_DATA = [
  // Banks
  { symbol: 'HDFCBANK', name: 'HDFC Bank', sector: 'Banks', capCategory: 'Large Cap' },
  { symbol: 'ICICIBANK', name: 'ICICI Bank', sector: 'Banks', capCategory: 'Large Cap' },
  { symbol: 'KOTAKBANK', name: 'Kotak Mahindra Bank', sector: 'Banks', capCategory: 'Large Cap' },
  { symbol: 'AXISBANK', name: 'Axis Bank', sector: 'Banks', capCategory: 'Large Cap' },

  // NBFCs
  { symbol: 'BAJFINANCE', name: 'Bajaj Finance', sector: 'NBFC', capCategory: 'Large Cap' },
  { symbol: 'CHOLAFIN', name: 'Cholamandalam Inv & Fin', sector: 'NBFC', capCategory: 'Large Cap' },
  { symbol: 'SHRIRAMFIN', name: 'Shriram Finance', sector: 'NBFC', capCategory: 'Large Cap' },
  { symbol: 'MUTHOOTFIN', name: 'Muthoot Finance', sector: 'NBFC', capCategory: 'Large Cap' },
  { symbol: 'ABCAPITAL', name: 'Aditya Birla Cap', sector: 'NBFC', capCategory: 'Mid Cap' },
  { symbol: 'M&MFIN', name: 'Mahindra & Mahindra Financial Services', sector: 'NBFC', capCategory: 'Mid Cap' },

  // Healthcare
  { symbol: 'APOLLOHOSP', name: 'Apollo Hospitals', sector: 'Healthcare', capCategory: 'Large Cap' },
  { symbol: 'MAXHEALTH', name: 'Max Healthcare', sector: 'Healthcare', capCategory: 'Large Cap' },

  // Pharmaceuticals
  { symbol: 'TORNTPHARM', name: 'Torrent Pharmaceuticals', sector: 'Pharmaceuticals', capCategory: 'Large Cap' },
  { symbol: 'DRREDDY', name: 'Dr Reddys Laboratories', sector: 'Pharmaceuticals', capCategory: 'Large Cap' },

  // IT Services
  { symbol: 'LTM', name: 'LTIMindtree', sector: 'IT Services', capCategory: 'Large Cap' },
  { symbol: 'TECHM', name: 'Tech Mahindra', sector: 'IT Services', capCategory: 'Large Cap' },

  // Autos / Auto Ancillaries
  { symbol: 'BAJAJ-AUTO', name: 'Bajaj Auto', sector: 'Autos / Auto Ancillaries', capCategory: 'Large Cap' },
  { symbol: 'MARUTI', name: 'Maruti Suzuki India', sector: 'Autos / Auto Ancillaries', capCategory: 'Large Cap' },
  { symbol: 'BOSCHLTD', name: 'Bosch Ltd', sector: 'Autos / Auto Ancillaries', capCategory: 'Large Cap' }
];

// Valuation methods mapping for sectors
const SECTOR_VALUATION_METHODS = {
  'Banks': 'ResidualIncome',
  'NBFC': 'PB-Valuation',
  'Healthcare': 'FCFF-DCF',
  'Pharmaceuticals': 'FCFF-DCF',
  'IT Services': 'FCFF-DCF',
  'Autos / Auto Ancillaries': 'FCFF-DCF'
};

// Valuation method descriptions
const VALUATION_METHOD_INFO = {
  'ResidualIncome': {
    name: 'Residual Income',
    description: 'Equity value equals current book value plus the present value of future residual income (PAT minus the equity charge) for the explicit horizon, plus a terminal residual-income perpetuity discounted to today. Equity charge uses CAPM cost of equity; terminal uses a stable cost of equity and terminal growth. Value per share is equity value divided by shares outstanding; margin of safety compares value per share to market price.',
    keyParams: ['basePAT', 'baseBV', 'basePATGrowth', 'dividendPayoutRatio', 'beta'],
    applicableSectors: ['Banks']
  },
  'FCFF-DCF': {
    name: 'Free Cash Flow to Firm (DCF)',
    description: 'This valuation follows a rigorous Free Cash Flow to Firm (FCFF) DCF framework, built bottom‑up from detailed operating drivers. Cash flows are derived explicitly as NOPAT plus depreciation, minus reinvestment in capital expenditure and working capital, ensuring full economic consistency. Reinvestment assumptions are anchored to ROIC‑based logic, with capital intensity and returns fading toward sustainable long‑term levels. Terminal value is tightly constrained by WACC and long‑term growth, avoiding overstated perpetuity assumptions. Non‑operating assets and net debt are adjusted transparently to arrive at intrinsic equity value per share. The methodology emphasizes discipline, realism, and auditability—making the valuation reliable for investors, advisors, and research professionals alike.',
    keyParams: ['baseFCFF', 'revenueGrowth', 'operatingMargin', 'capexAsPercentOfRevenue', 'workingCapitalChange', 'wacc'],
    applicableSectors: ['Healthcare', 'Pharmaceuticals', 'IT Services', 'Autos / Auto Ancillaries']
  },
  'PB-Valuation': {
    name: 'P/B Scorecard Valuation',
    description: 'This valuation estimates a fair price by linking Price‑to‑Book (P/B) to a company\'s profitability, growth, risk, and balance‑sheet strength. We begin with a Base P/B, anchored to long‑term profitability (primarily ROE, supported by ROA, margins, and cost efficiency). This base is then adjusted using three structured multipliers: Growth (driven by AUM growth), Asset Quality (driven by GNPA, credit cost, and provisioning), and Capital & Funding (driven by capital adequacy and leverage). A small Governance overlay is added to reflect qualitative strength. To ensure discipline, the resulting P/B is capped by a Max P/B.',
    formula: 'Justified P/B = Base P/B × Growth × Asset Quality × Capital/Funding + Governance Overlay (capped at Max P/B); Fair Price = Justified P/B × BVPS',
    keyLevers: [
      'ROE (%) — higher expected ROE generally supports a higher Base P/B (profitability strength)',
      'AUM CAGR (%) — higher expected AUM growth lifts the Growth Multiplier (growth premium)',
      'GNPA (%) — higher GNPA weakens the Asset Quality Multiplier (credit risk penalty)',
      'Debt‑to‑Equity (x) — higher leverage can reduce the Capital & Funding Multiplier (funding risk / balance sheet risk)'
    ],
    transparencyNote: 'The four levers are scenario tweaks around a base case. The base case itself is built using a broader scorecard (profitability, growth, asset quality, capital/funding, governance). We display those inputs so you can see where the base valuation comes from.',
    whatThisIsNot: 'This is not a DCF and does not forecast detailed cash flows year by year. It is a structured P/B framework that links valuation to profitability, growth, risk, and balance sheet strength using a scorecard.',
    applicableSectors: ['NBFC']
  }
};

const SECTORS_INFO = computed(() => {
  const sectorCounts = {};
  STOCK_DATA.forEach(stock => {
    sectorCounts[stock.sector] = (sectorCounts[stock.sector] || 0) + 1;
  });

  return Object.keys(sectorCounts).map(sector => ({
    name: sector,
    count: sectorCounts[sector],
    valuationMethod: SECTOR_VALUATION_METHODS[sector] || 'ResidualIncome',
    methodInfo: VALUATION_METHOD_INFO[SECTOR_VALUATION_METHODS[sector]] || VALUATION_METHOD_INFO['ResidualIncome']
  }));
});

// Component state
const selectedSector = ref(null);
const selectedStock = ref('');
const activeTab = ref('overview');

const loading = ref(false);
const error = ref('');

const baseParams = ref(null);
const techData = ref(null);

// Interactive overrides (in % deltas around base)
const patGrowthDeltaPct = ref(0);
const patGrowthYears = ref(5);

// FCFF-DCF scenario state
const fcffSalesGrowthDeltaPct = ref(0);
const fcffDepPctDelta = ref(0);
const fcffCapexPctDelta = ref(0);
const fcffDeltaWcPctDelta = ref(0);
const fcffForecastYears = ref(5);

// P/B valuation state (absolute overrides)
const pbDrivers = ref({
  roeAvgPct: null,
  aumCagrPct: null,
  gnpaPct: null,
  debtToEquity: null
});
const showScorecardDetails = ref(false);
const showCategoryInsights = ref(false);

// NBFC Category insights
const NBFC_CATEGORY_INSIGHTS = {
  Retail: {
    summary: [
      "Retail lenders in this dataset show strong growth premiums (Growth Multiplier around 1.10) but outcomes diverge based on asset quality and leverage.",
      "Expect AUM growth to matter, but GNPA and leverage can quickly compress valuation."
    ],
    datasetExamples: [
      "BAJFINANCE (Retail): ROE 19.00%, AUM CAGR 23.14%, GNPA 0.90%, D/E 3.85; Base_PB 4.5; Multipliers: Growth 1.10, AssetQ 1.15, Capital/Funding 1.05; GovOverlay 0.5; MaxPB 8; JustifiedPB 6.477; FairPrice 1075.20.",
      "CHOLAFIN (Retail): ROE 18.67%, AUM CAGR 23.50%, GNPA 4.26%, D/E 7.80; Base_PB 4.0; Multipliers: Growth 1.10, AssetQ 1.00, Capital/Funding 1.00; GovOverlay 0.4; MaxPB 8; JustifiedPB 4.8; FairPrice 1473.60."
    ],
    observedRangesThisDataset: {
      roeAvgPct: "18.67% – 19.00%",
      aumCagrPct: "23.14% – 23.50%",
      gnpaPct: "0.90% – 4.26%",
      debtToEquity: "3.85x – 7.80x"
    }
  },
  Cyclical: {
    summary: [
      "Cyclical lenders in this dataset carry higher credit risk (higher GNPA) and therefore lower growth/asset-quality multipliers.",
      "Valuation caps (Max P/B) are tighter, reflecting risk cyclicality."
    ],
    datasetExamples: [
      "SHRIRAMFIN (Cyclical): ROE 13.53%, AUM CAGR 17.47%, GNPA 5.43%, D/E 3.80; Base_PB 3.0; Multipliers: Growth 0.90, AssetQ 0.765, Capital/Funding 1.05; GovOverlay 0.4; MaxPB 5; JustifiedPB 2.569; FairPrice 827.15."
    ],
    observedRangesThisDataset: {
      roeAvgPct: "13.53%",
      aumCagrPct: "17.47%",
      gnpaPct: "5.43%",
      debtToEquity: "3.80x"
    }
  },
  Gold: {
    summary: [
      "Gold lenders in this dataset show high ROE and supportive capital/funding, but lower growth multipliers (growth is not the primary value driver).",
      "Max P/B caps can be moderate, reflecting a more bounded valuation profile."
    ],
    datasetExamples: [
      "MUTHOOTFIN (Gold): ROE 22.18%, AUM CAGR 18.22%, GNPA 3.53%, D/E 3.10; Base_PB 6.0; Multipliers: Growth 0.70, AssetQ 1.10, Capital/Funding 1.10; GovOverlay 0.0; MaxPB 6; JustifiedPB 5.082; FairPrice 4507.73."
    ],
    observedRangesThisDataset: {
      roeAvgPct: "22.18%",
      aumCagrPct: "18.22%",
      gnpaPct: "3.53%",
      debtToEquity: "3.10x"
    }
  },
  Rural: {
    summary: [
      "Rural lenders in this dataset show lower profitability (ROE) and moderate risk, leading to lower base P/B and tighter valuation ceilings.",
      "Leverage and asset quality can matter more than growth in keeping valuation stable."
    ],
    datasetExamples: [
      "M&MFIN (Rural): ROE 8.35%, AUM CAGR 11.96%, GNPA 3.80%, D/E 4.90; Base_PB 2.0; Multipliers: Growth 0.76, AssetQ 0.80, Capital/Funding 1.00; GovOverlay 0.4; MaxPB 4.5; JustifiedPB 1.616; FairPrice 287.65."
    ],
    observedRangesThisDataset: {
      roeAvgPct: "8.35%",
      aumCagrPct: "11.96%",
      gnpaPct: "3.80%",
      debtToEquity: "4.90x"
    }
  },
  Mixed: {
    summary: [
      "Mixed/holding-company profiles in this dataset show lower growth multipliers and moderate asset-quality multipliers with tighter Max P/B caps.",
      "Valuation sensitivity can be driven by both leverage and profitability."
    ],
    datasetExamples: [
      "ABCAPITAL (Mixed): ROE 11.05%, AUM CAGR 11.16%, GNPA 2.47%, D/E 4.90; Base_PB 2.2; Multipliers: Growth 0.665, AssetQ 0.90, Capital/Funding 1.00; GovOverlay 0.4; MaxPB 4; JustifiedPB 1.7167; FairPrice 211.15."
    ],
    observedRangesThisDataset: {
      roeAvgPct: "11.05%",
      aumCagrPct: "11.16%",
      gnpaPct: "2.47%",
      debtToEquity: "4.90x"
    }
  },
  disclaimer: [
    "These category notes are illustrative examples from the current dataset in the XLS, not universal category rules.",
    "Your base case uses trailing averages from the scorecard; sliders are forward-looking scenario adjustments around that base."
  ]
};

const currentCategoryInsight = computed(() => {
  if (!baseParams.value?.scorecardSnapshot?.nbfcCategory) return null;
  const category = baseParams.value.scorecardSnapshot.nbfcCategory;
  return NBFC_CATEGORY_INSIGHTS[category] || null;
});

const selectedStockData = computed(() => {
  return STOCK_DATA.find(stock => stock.symbol === selectedStock.value);
});

const selectedStockValuationMethod = computed(() => {
  if (!selectedStockData.value) return 'ResidualIncome';
  return SECTOR_VALUATION_METHODS[selectedStockData.value.sector] || 'ResidualIncome';
});

const selectedValuationMethodInfo = computed(() => {
  return VALUATION_METHOD_INFO[selectedStockValuationMethod.value];
});

const filteredStocks = computed(() => {
  if (!selectedSector.value) return STOCK_DATA;
  return STOCK_DATA.filter(stock => stock.sector === selectedSector.value);
});

const valuationInput = computed(() => {
  if (!baseParams.value) return null;

  // P/B Valuation for NBFCs
  if (selectedStockValuationMethod.value === 'PB-Valuation') {
    return {
      apiPayload: { valuationParams: baseParams.value },
      driversOverride: {
        roeAvgPct: pbDrivers.value.roeAvgPct,
        aumCagrPct: pbDrivers.value.aumCagrPct,
        gnpaPct: pbDrivers.value.gnpaPct,
        debtToEquity: pbDrivers.value.debtToEquity
      }
    };
  }

  if (selectedStockValuationMethod.value === 'FCFF-DCF') {
    // FCFF-DCF valuation input - apply user deltas to backend params
    const drivers = baseParams.value.drivers || {};
    const fixed = baseParams.value.fixed || {};

    // Apply deltas to first element of arrays for user-adjustable drivers
    const adjustedDrivers = {
      salesGrowthPct: drivers.salesGrowthPct ? [...drivers.salesGrowthPct] : [],
      depPctSales: drivers.depPctSales ? [...drivers.depPctSales] : [],
      capexPctSales: drivers.capexPctSales ? [...drivers.capexPctSales] : [],
      deltaWcPctSales: drivers.deltaWcPctSales ? [...drivers.deltaWcPctSales] : []
    };

    // Apply user deltas only to the first year values
    if (adjustedDrivers.salesGrowthPct.length > 0) {
      adjustedDrivers.salesGrowthPct = adjustedDrivers.salesGrowthPct.map((v, i) =>
        i === 0 ? v + fcffSalesGrowthDeltaPct.value : v
      );
    }
    if (adjustedDrivers.depPctSales.length > 0) {
      adjustedDrivers.depPctSales = adjustedDrivers.depPctSales.map((v, i) =>
        i === 0 ? v + fcffDepPctDelta.value : v
      );
    }
    if (adjustedDrivers.capexPctSales.length > 0) {
      adjustedDrivers.capexPctSales = adjustedDrivers.capexPctSales.map((v, i) =>
        i === 0 ? v + fcffCapexPctDelta.value : v
      );
    }
    if (adjustedDrivers.deltaWcPctSales.length > 0) {
      adjustedDrivers.deltaWcPctSales = adjustedDrivers.deltaWcPctSales.map((v, i) =>
        i === 0 ? v + fcffDeltaWcPctDelta.value : v
      );
    }

    return {
      baseYear: baseParams.value.baseYear,
      baseSales: baseParams.value.baseSales,
      forecastYears: fcffForecastYears.value,
      drivers: adjustedDrivers,
      fixed: {
        ebitMarginPct: fixed.ebitMarginPct || [],
        taxRatePct: fixed.taxRatePct || [],
        terminalGrowthPct: fixed.terminalGrowthPct || 0,
        waccPct: fixed.waccPct || 0,
        waccStablePct: fixed.waccStablePct || 0
      },
      bridge: baseParams.value.bridge || {},
      currentPrice: baseParams.value.currentPrice
    };
  }
  // Residual Income valuation input
  return {
    stockName: selectedStock.value,
    params: baseParams.value,
    patGrowthDeltaPct: patGrowthDeltaPct.value,
    patGrowthYears: patGrowthYears.value,
  };
});

const valuation = computed(() => {
  if (!valuationInput.value) return null;
  try {
    // P/B Valuation for NBFCs
    if (selectedStockValuationMethod.value === 'PB-Valuation') {
      const pbResult = computePBValuation(valuationInput.value);
      // Transform to match expected structure for dial/overview
      return {
        valPerShare: pbResult.fairPrice,
        marginOfSafety: pbResult.marginOfSafety || 0,
        params: {
          currentPrice: baseParams.value.currentPrice || 0
        },
        ...pbResult
      };
    }

    if (selectedStockValuationMethod.value === 'FCFF-DCF') {
      // Wrap FCFF params in params object as expected by computeFCFFValuation
      return computeFCFFValuation({ params: valuationInput.value });
    }
    return computeValuationFromAPI(valuationInput.value);
  } catch (e) {
    console.error('valuation error', e);
    return null;
  }
});

const mosPct = computed(() => {
  if (!valuation.value) return null;
  return valuation.value.marginOfSafety * 100;
});

const mosLabel = computed(() => {
  if (mosPct.value === null) return '';
  if (mosPct.value > 10) return 'Undervalued';
  if (mosPct.value < -10) return 'Overvalued';
  return 'Fair value zone';
});

const fairZone = { low: -10, high: 10 };

const dialAngle = computed(() => {
  if (mosPct.value === null) return 0;
  const clamped = Math.max(-60, Math.min(60, mosPct.value));
  return (clamped / 60) * 90;
});

// Methods
const onSectorSelected = (sector) => {
  selectedSector.value = sector;
  selectedStock.value = '';
  baseParams.value = null;
  techData.value = null;
  activeTab.value = 'overview';
};

const onStockSelected = (symbol) => {
  selectedStock.value = symbol;
  activeTab.value = 'overview';
  fetchValuation(symbol);
};

const onTabChanged = (tabId) => {
  activeTab.value = tabId;
};

const fetchValuation = async (symbol) => {
  if (!symbol) return;
  loading.value = true;
  error.value = '';
  baseParams.value = null;
  techData.value = null;
  try {
    const resp = await userStore.getStockValuationParams(symbol);
    const payload = resp?.responseData || resp || {};

    // For FCFF-DCF stocks, the entire payload is the valuation params
    // For ResidualIncome stocks, it's under valuationParams
    const vp = payload.valuationParams || payload;
    const currentPrice = payload.currentPrice || vp.currentPrice;

    baseParams.value = {
      ...vp,
      currentPrice,
    };

    patGrowthDeltaPct.value = 0;
    patGrowthYears.value = 5;
    fcffSalesGrowthDeltaPct.value = 0;
    fcffDepPctDelta.value = 0;
    fcffCapexPctDelta.value = 0;
    fcffDeltaWcPctDelta.value = 0;
    fcffForecastYears.value = vp.forecastYears || 5;

    // Reset P/B drivers to base values for NBFCs
    if (vp.valuationCore?.baseDrivers) {
      pbDrivers.value = {
        roeAvgPct: vp.valuationCore.baseDrivers.roeAvgPct,
        aumCagrPct: vp.valuationCore.baseDrivers.aumCagrPct,
        gnpaPct: vp.valuationCore.baseDrivers.gnpaPct,
        debtToEquity: vp.valuationCore.baseDrivers.debtToEquity
      };
    }
    showScorecardDetails.value = false;
    showCategoryInsights.value = false;

    // fetch technical indicators in parallel
    const techResp = await userStore.getStockTechData(symbol);
    techData.value = techResp?.responseData || techResp || null;
  } catch (e) {
    console.error(e);
    error.value = 'Could not load valuation inputs. Please try again.';
  } finally {
    loading.value = false;
  }
};

// Format helpers
const formatNumber = (val) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return val;
  return Number(val).toLocaleString('en-IN');
};

const formatDecimal = (val, digits = 2) => {
  if (val === null || val === undefined || Number.isNaN(Number(val))) return val;
  const num = Number(val).toFixed(digits);
  return Number(num).toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const isStockDialogOpen = ref(false);
const stockDialogData = ref(null);

const openStockDialog = (stock) => {
  stockDialogData.value = stock;
  isStockDialogOpen.value = true;
};
</script>

<template>
  <div class="stock-analysis-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="discovery-flow">
        <div class="flow-step">
          <i class="pi pi-th-large"></i>
          <span>Choose Sector</span>
        </div>
        <i class="pi pi-chevron-right flow-arrow"></i>
        <div class="flow-step">
          <i class="pi pi-building"></i>
          <span>Select Stock</span>
        </div>
        <i class="pi pi-chevron-right flow-arrow"></i>
        <div class="flow-step analysis-step">
          <i class="pi pi-chart-line"></i>
          <span>Analyze</span>
          <span class="analysis-types">Valuation • Technical • Portfolio</span>
        </div>
      </div>
    </div>

    <!-- Sector Discovery Rail -->
    <SectorDiscoveryRail
      :sectors="SECTORS_INFO"
      :selectedSector="selectedSector"
      @sectorSelected="onSectorSelected"
    />

    <!-- Stock Discovery Panel -->
    <StockDiscoveryPanel
      v-if="selectedSector || STOCK_DATA.length <= 20"
      :stocks="STOCK_DATA"
      :selectedSector="selectedSector"
      :selectedStock="selectedStock"
      @stockSelected="onStockSelected"
    />

    <!-- Analysis Section -->
    <div v-if="!selectedStock" class="analysis-section">
      <EmptyAnalysisState />
    </div>

    <div v-else class="analysis-section">
      <AnalysisTabs :defaultTab="activeTab" @tabChanged="onTabChanged">
        <template #default="{ activeTab }">
          <!-- Overview Tab -->
          <div v-if="activeTab === 'overview' && selectedStockData && valuation && baseParams">
            <StockOverview
              :symbol="selectedStock"
              :stockName="selectedStockData.name"
              :sector="selectedStockData.sector"
              :currentPrice="baseParams.currentPrice || 0"
              :intrinsicValue="valuation.valPerShare || 0"
              :marginOfSafety="mosPct || 0"
              :openStockDialog="openStockDialog"
            />
          </div>

          <div v-else-if="activeTab === 'overview' && selectedStock && loading" class="loading-section">
            <div class="loading-spinner">
              <i class="pi pi-spinner pi-spin"></i>
            </div>
            <p>Loading stock overview...</p>
          </div>

          <!-- Valuation Tab -->
          <div v-else-if="activeTab === 'valuation'">
            <div v-if="valuation && baseParams" class="valuation-section">
              <div class="intrinsic-grids">
                <div class="intrinsic-card">
                  <!-- Fair value dial -->
                  <div class="sav-dial-card">
                    <div class="sav-dial-header">
                      <h3 class="sav-card-title">Fair value dial</h3>
                      <div class="sav-stockname-bar" v-if="selectedStock">
                        <span class="sav-stockname-value">{{ selectedStock }}</span>
                      </div>
                    </div>
                    <div class="sav-dial-wrap">
                      <div class="sav-dial">
                        <div class="sav-dial-arc sav-dial-arc--fair" />
                        <div class="sav-dial-needle" :style="{ transform: `rotate(${dialAngle || 0}deg)` }" />
                      </div>
                      <div class="sav-dial-legend">
                        <span class="badge badge-overvalued">Overvalued</span>
                        <span class="badge badge-fair">Fair (±{{ fairZone.high }}%)</span>
                        <span class="badge badge-undervalued">Undervalued</span>
                      </div>
                    </div>
                    <div class="sav-dial-meta" v-if="valuation">
                      <div>
                        <div class="label">Intrinsic value / share</div>
                        <div class="value">₹ {{ formatDecimal(valuation?.valPerShare ?? 0, 2) }}</div>
                      </div>
                      <div>
                        <div class="label">Current price</div>
                        <div class="value">₹ {{ formatDecimal((valuation?.params?.currentPrice ?? baseParams.currentPrice) || 0, 2) }}</div>
                      </div>
                      <div>
                        <div class="label">Margin of safety</div>
                        <div class="value" :class="{ pos: (mosPct || 0) > 0, neg: (mosPct || 0) < 0 }">
                          {{ (mosPct || 0).toFixed(1) }}%
                          <span class="mos-label">{{ mosLabel }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Valuation method block -->
                  <div class="sav-method-card">
                    <h3 class="sav-card-title">Valuation method: {{ selectedValuationMethodInfo.name }}</h3>
                    <div class="method-badge" :class="`method-${selectedStockValuationMethod.toLowerCase().replace(/[^a-z]/g, '-')}`">
                      {{ selectedStockValuationMethod }}
                    </div>
                    <p class="sav-method-text">
                      {{ selectedValuationMethodInfo.description }}
                    </p>
                  </div>
                </div>

                <div class="intrinsic-card" v-if="baseParams">
                  <!-- Scenario controls card -->
                  <div class="sav-card">
                    <h3 class="sav-card-title">Scenario controls (horizon)</h3>

                    <template v-if="selectedStockValuationMethod === 'ResidualIncome'">
                      <div class="sav-slider-row" title="Change PAT growth to see bull/bear valuation.">
                        <div class="slider-header slider-header--stacked">
                          <div class="slider-title">Base Profit-After-Tax (PAT) growth (per year)</div>
                          <div class="slider-value">
                            Base: {{ baseParams.basePATGrowth }}% · Delta: {{ patGrowthDeltaPct.toFixed(0) }}% ·
                            Effective: {{ (baseParams.basePATGrowth + patGrowthDeltaPct).toFixed(1) }}%
                          </div>
                        </div>
                        <input
                          v-model.number="patGrowthDeltaPct"
                          type="range"
                          min="-10"
                          max="10"
                          step="1"
                          class="sav-slider"
                        />
                        <div class="slider-scale">
                          <span>Bear (-10%)</span>
                          <span>Base</span>
                          <span>Bull (+10%)</span>
                        </div>
                      </div>

                      <div class="sav-slider-row" title="Number of years the stock enjoys the explicit PAT growth before terminal growth applies.">
                        <div class="slider-header slider-header--stacked">
                          <div class="slider-title">PAT growth years</div>
                          <div class="slider-value">
                            {{ patGrowthYears.toFixed(0) }} years of explicit growth
                          </div>
                        </div>
                        <input
                          v-model.number="patGrowthYears"
                          type="range"
                          min="3"
                          max="10"
                          step="1"
                          class="sav-slider"
                        />
                        <div class="slider-scale">
                          <span>Short (3 yrs)</span>
                          <span>Base (5 yrs)</span>
                          <span>Extended (10 yrs)</span>
                        </div>
                      </div>

                      <div class="sav-base-grid">
                        <div class="base-item">
                          <div class="label">Base PAT (₹ cr)</div>
                          <div class="value">{{ formatNumber(baseParams.basePAT) }}</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Book value (₹ cr)</div>
                          <div class="value">{{ formatNumber(baseParams.baseBV) }}</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Shares outstanding (cr)</div>
                          <div class="value">{{ formatNumber(baseParams.sharesOutstanding) }}</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Beta</div>
                          <div class="value">{{ baseParams.beta }}</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Risk-free rate</div>
                          <div class="value">{{ formatDecimal(baseParams.riskFreeRate, 2) }}%</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Equity risk premium</div>
                          <div class="value">{{ formatDecimal(baseParams.equityRiskPremium, 2) }}%</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Terminal growth</div>
                          <div class="value">{{ formatDecimal(baseParams.terminalGrowthRate, 2) }}%</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Dividend payout</div>
                          <div class="value">{{ formatDecimal(baseParams.dividendPayoutRatio, 0) }}%</div>
                        </div>
                      </div>
                    </template>

                    <template v-else-if="selectedStockValuationMethod === 'FCFF-DCF'">
                      <div class="sav-slider-row" title="Change sales growth to see bull/bear valuation.">
                        <div class="slider-header slider-header--stacked">
                          <div class="slider-title">Base Sales Growth (per year)</div>
                          <div class="slider-value">
                            Base: {{ baseParams.drivers.salesGrowthPct[0] }}% · Delta: {{ fcffSalesGrowthDeltaPct.toFixed(1) }}% ·
                            Effective: {{ (baseParams.drivers.salesGrowthPct[0] + fcffSalesGrowthDeltaPct).toFixed(1) }}%
                          </div>
                        </div>
                        <input
                          v-model.number="fcffSalesGrowthDeltaPct"
                          type="range"
                          :min="-Math.abs(baseParams.drivers.salesGrowthPct[0]) * 0.2"
                          :max="Math.abs(baseParams.drivers.salesGrowthPct[0]) * 0.2"
                          step="0.1"
                          class="sav-slider"
                        />
                        <div class="slider-scale">
                          <span>Bear</span>
                          <span>Base</span>
                          <span>Bull</span>
                        </div>
                      </div>

                      <div class="sav-slider-row" title="Change depreciation % of sales.">
                        <div class="slider-header slider-header--stacked">
                          <div class="slider-title">Depreciation (% of Sales)</div>
                          <div class="slider-value">
                            Base: {{ baseParams.drivers.depPctSales[0] }}% · Delta: {{ fcffDepPctDelta.toFixed(2) }}% ·
                            Effective: {{ (baseParams.drivers.depPctSales[0] + fcffDepPctDelta).toFixed(2) }}%
                          </div>
                        </div>
                        <input
                          v-model.number="fcffDepPctDelta"
                          type="range"
                          :min="-Math.abs(baseParams.drivers.depPctSales[0]) * 0.2"
                          :max="Math.abs(baseParams.drivers.depPctSales[0]) * 0.2"
                          step="0.01"
                          class="sav-slider"
                        />
                        <div class="slider-scale">
                          <span>Bear</span>
                          <span>Base</span>
                          <span>Bull</span>
                        </div>
                      </div>

                      <div class="sav-slider-row" title="Change capex % of sales.">
                        <div class="slider-header slider-header--stacked">
                          <div class="slider-title">Capex (% of Sales)</div>
                          <div class="slider-value">
                            Base: {{ baseParams.drivers.capexPctSales[0] }}% · Delta: {{ fcffCapexPctDelta.toFixed(2) }}% ·
                            Effective: {{ (baseParams.drivers.capexPctSales[0] + fcffCapexPctDelta).toFixed(2) }}%
                          </div>
                        </div>
                        <input
                          v-model.number="fcffCapexPctDelta"
                          type="range"
                          :min="-Math.abs(baseParams.drivers.capexPctSales[0]) * 0.2"
                          :max="Math.abs(baseParams.drivers.capexPctSales[0]) * 0.2"
                          step="0.01"
                          class="sav-slider"
                        />
                        <div class="slider-scale">
                          <span>Bear</span>
                          <span>Base</span>
                          <span>Bull</span>
                        </div>
                      </div>

                      <div class="sav-slider-row" title="Change working capital delta % of sales.">
                        <div class="slider-header slider-header--stacked">
                          <div class="slider-title">Δ Working Capital (% of Sales)</div>
                          <div class="slider-value">
                            Base: {{ baseParams.drivers.deltaWcPctSales[0] || 0 }}% · Delta: {{ fcffDeltaWcPctDelta.toFixed(2) }}% ·
                            Effective: {{ ((baseParams.drivers.deltaWcPctSales[0] || 0) + fcffDeltaWcPctDelta).toFixed(2) }}%
                          </div>
                        </div>
                        <input
                          v-model.number="fcffDeltaWcPctDelta"
                          type="range"
                          :min="-Math.abs(baseParams.drivers.deltaWcPctSales[0] || 1) * 0.2"
                          :max="Math.abs(baseParams.drivers.deltaWcPctSales[0] || 1) * 0.2"
                          step="0.01"
                          class="sav-slider"
                        />
                        <div class="slider-scale">
                          <span>Bear</span>
                          <span>Base</span>
                          <span>Bull</span>
                        </div>
                      </div>

                      <div class="sav-slider-row" title="Forecast years for explicit horizon.">
                        <div class="slider-header slider-header--stacked">
                          <div class="slider-title">Forecast years</div>
                          <div class="slider-value">
                            {{ fcffForecastYears }} years
                          </div>
                        </div>
                        <input
                          v-model.number="fcffForecastYears"
                          type="range"
                          min="3"
                          max="10"
                          step="1"
                          class="sav-slider"
                        />
                        <div class="slider-scale">
                          <span>Short (3 yrs)</span>
                          <span>Base (5 yrs)</span>
                          <span>Extended (10 yrs)</span>
                        </div>
                      </div>

                      <div class="sav-base-grid">
                        <div class="base-item">
                          <div class="label">Base Year</div>
                          <div class="value">{{ baseParams.baseYear }}</div>
                        </div>
                        <div class="base-item">
                          <div class="label">Base Sales (₹ cr)</div>
                          <div class="value">{{ formatNumber(baseParams.baseSales) }}</div>
                        </div>
                        <div class="base-item">
                          <div class="label">WACC (Stable)</div>
                          <div class="value">{{ formatDecimal(baseParams.fixed.waccStablePct, 2) }}%</div>
                        </div>
                      </div>
                    </template>

                    <template v-else-if="selectedStockValuationMethod === 'PB-Valuation'">
                      <!-- P/B Valuation Controls -->
                      <div class="pb-valuation-controls">
                        <div class="pb-levers-note">
                          <p class="font-12 text-gray mb-10">
                            <strong>Key Levers:</strong> The four drivers below are scenario tweaks around a base case built from a broader scorecard (profitability, growth, asset quality, capital/funding, governance). Adjust these to see forward‑looking valuation scenarios.
                          </p>
                        </div>

                        <!-- ROE Slider -->
                        <div class="sav-slider-row" v-if="baseParams.valuationCore?.driverRanges?.roeAvgPct">
                          <div class="slider-header slider-header--stacked">
                            <div class="slider-title">ROE (%)</div>
                            <div class="slider-value">
                              Current: {{ pbDrivers.roeAvgPct?.toFixed(2) || baseParams.valuationCore.baseDrivers.roeAvgPct }}%
                            </div>
                          </div>
                          <input
                            v-model.number="pbDrivers.roeAvgPct"
                            type="range"
                            :min="baseParams.valuationCore.driverRanges.roeAvgPct.min"
                            :max="baseParams.valuationCore.driverRanges.roeAvgPct.max"
                            :step="baseParams.valuationCore.driverRanges.roeAvgPct.step"
                            class="sav-slider"
                          />
                          <div class="slider-scale">
                            <span>{{ baseParams.valuationCore.driverRanges.roeAvgPct.min }}%</span>
                            <span>Base: {{ baseParams.valuationCore.baseDrivers.roeAvgPct }}%</span>
                            <span>{{ baseParams.valuationCore.driverRanges.roeAvgPct.max }}%</span>
                          </div>
                          <p class="slider-hint">Higher ROE supports higher Base P/B (profitability strength)</p>
                        </div>

                        <!-- AUM CAGR Slider -->
                        <div class="sav-slider-row" v-if="baseParams.valuationCore?.driverRanges?.aumCagrPct">
                          <div class="slider-header slider-header--stacked">
                            <div class="slider-title">AUM CAGR (%)</div>
                            <div class="slider-value">
                              Current: {{ pbDrivers.aumCagrPct?.toFixed(2) || baseParams.valuationCore.baseDrivers.aumCagrPct }}%
                            </div>
                          </div>
                          <input
                            v-model.number="pbDrivers.aumCagrPct"
                            type="range"
                            :min="baseParams.valuationCore.driverRanges.aumCagrPct.min"
                            :max="baseParams.valuationCore.driverRanges.aumCagrPct.max"
                            :step="baseParams.valuationCore.driverRanges.aumCagrPct.step"
                            class="sav-slider"
                          />
                          <div class="slider-scale">
                            <span>{{ baseParams.valuationCore.driverRanges.aumCagrPct.min }}%</span>
                            <span>Base: {{ baseParams.valuationCore.baseDrivers.aumCagrPct }}%</span>
                            <span>{{ baseParams.valuationCore.driverRanges.aumCagrPct.max }}%</span>
                          </div>
                          <p class="slider-hint">Higher AUM growth lifts Growth Multiplier (growth premium)</p>
                        </div>

                        <!-- GNPA Slider -->
                        <div class="sav-slider-row" v-if="baseParams.valuationCore?.driverRanges?.gnpaPct">
                          <div class="slider-header slider-header--stacked">
                            <div class="slider-title">GNPA (%)</div>
                            <div class="slider-value">
                              Current: {{ pbDrivers.gnpaPct?.toFixed(2) || baseParams.valuationCore.baseDrivers.gnpaPct }}%
                            </div>
                          </div>
                          <input
                            v-model.number="pbDrivers.gnpaPct"
                            type="range"
                            :min="baseParams.valuationCore.driverRanges.gnpaPct.min"
                            :max="baseParams.valuationCore.driverRanges.gnpaPct.max"
                            :step="baseParams.valuationCore.driverRanges.gnpaPct.step"
                            class="sav-slider"
                          />
                          <div class="slider-scale">
                            <span>{{ baseParams.valuationCore.driverRanges.gnpaPct.min }}%</span>
                            <span>Base: {{ baseParams.valuationCore.baseDrivers.gnpaPct }}%</span>
                            <span>{{ baseParams.valuationCore.driverRanges.gnpaPct.max }}%</span>
                          </div>
                          <p class="slider-hint">Higher GNPA weakens Asset Quality Multiplier (credit risk penalty)</p>
                        </div>

                        <!-- Debt-to-Equity Slider -->
                        <div class="sav-slider-row" v-if="baseParams.valuationCore?.driverRanges?.debtToEquity">
                          <div class="slider-header slider-header--stacked">
                            <div class="slider-title">Debt-to-Equity (x)</div>
                            <div class="slider-value">
                              Current: {{ pbDrivers.debtToEquity?.toFixed(2) || baseParams.valuationCore.baseDrivers.debtToEquity }}x
                            </div>
                          </div>
                          <input
                            v-model.number="pbDrivers.debtToEquity"
                            type="range"
                            :min="baseParams.valuationCore.driverRanges.debtToEquity.min"
                            :max="baseParams.valuationCore.driverRanges.debtToEquity.max"
                            :step="baseParams.valuationCore.driverRanges.debtToEquity.step"
                            class="sav-slider"
                          />
                          <div class="slider-scale">
                            <span>{{ baseParams.valuationCore.driverRanges.debtToEquity.min }}x</span>
                            <span>Base: {{ baseParams.valuationCore.baseDrivers.debtToEquity }}x</span>
                            <span>{{ baseParams.valuationCore.driverRanges.debtToEquity.max }}x</span>
                          </div>
                          <p class="slider-hint">Higher leverage reduces Capital & Funding Multiplier (balance sheet risk)</p>
                        </div>

                        <!-- Valuation Outputs -->
                        <div class="pb-outputs-grid" v-if="valuation">
                          <div class="pb-output-item">
                            <div class="label">Fair Price</div>
                            <div class="value text-primary">₹{{ formatDecimal(valuation.fairPrice, 2) }}</div>
                          </div>
                          <div class="pb-output-item">
                            <div class="label">Justified P/B</div>
                            <div class="value">{{ formatDecimal(valuation.justifiedPB, 3) }}</div>
                          </div>
                          <div class="pb-output-item" v-if="valuation.marginOfSafety !== null">
                            <div class="label">Margin of Safety</div>
                            <div class="value" :class="{ pos: (mosPct || 0) > 0, neg: (mosPct || 0) < 0 }">
                              {{ (mosPct || 0).toFixed(1) }}%
                            </div>
                          </div>
                        </div>

                        <!-- Scorecard Details Accordion -->
                        <div class="scorecard-accordion" v-if="baseParams.scorecardSnapshot">
                          <button
                            class="accordion-toggle"
                            @click="showScorecardDetails = !showScorecardDetails"
                          >
                            <span>{{ showScorecardDetails ? '▼' : '▶' }} View Scorecard Details (Base Case Inputs)</span>
                          </button>

                          <div v-show="showScorecardDetails" class="accordion-content">
                            <!-- NBFC Category -->
                            <div class="scorecard-section" v-if="baseParams.scorecardSnapshot.nbfcCategory">
                              <h5 class="scorecard-section-title">NBFC Category: {{ baseParams.scorecardSnapshot.nbfcCategory }}</h5>
                              <p class="font-12 text-gray">Category influences typical profitability, growth, and risk ranges.</p>
                            </div>

                            <!-- Profitability Pillar -->
                            <div class="scorecard-section" v-if="baseParams.scorecardSnapshot.profitabilityPillar">
                              <h5 class="scorecard-section-title">📊 Profitability Pillar (Base P/B)</h5>
                              <div class="scorecard-grid">
                                <div class="scorecard-item">
                                  <span class="label">ROE Avg</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.profitabilityPillar.roeAvg * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">ROA Avg</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.profitabilityPillar.roaAvg * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">NIM 3Y Avg</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.profitabilityPillar.nim3yAvg * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">Cost-to-Income</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.profitabilityPillar.cti3yAvg * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item highlight">
                                  <span class="label">Base P/B</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.profitabilityPillar.basePB.toFixed(2) }}</span>
                                </div>
                              </div>
                              <p class="scorecard-note">Base P/B is anchored to profitability (ROE, ROA, margins, efficiency)</p>
                            </div>

                            <!-- Growth Pillar -->
                            <div class="scorecard-section" v-if="baseParams.scorecardSnapshot.growthPillar">
                              <h5 class="scorecard-section-title">📈 Growth Pillar</h5>
                              <div class="scorecard-grid">
                                <div class="scorecard-item">
                                  <span class="label">AUM CAGR</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.growthPillar.aumCagr * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">Growth Score</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.growthPillar.growthScore }}</span>
                                </div>
                                <div class="scorecard-item highlight">
                                  <span class="label">Growth Multiplier</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.growthPillar.growthMultiplier.toFixed(3) }}</span>
                                </div>
                              </div>
                              <p class="scorecard-note">Growth affects valuation via a multiplier based on AUM expansion</p>
                            </div>

                            <!-- Asset Quality Pillar -->
                            <div class="scorecard-section" v-if="baseParams.scorecardSnapshot.assetQualityPillar">
                              <h5 class="scorecard-section-title">🛡️ Asset Quality Pillar</h5>
                              <div class="scorecard-grid">
                                <div class="scorecard-item">
                                  <span class="label">GNPA 3Y Avg</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.assetQualityPillar.gnpa3yAvg * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">Credit Cost 3Y</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.assetQualityPillar.creditCost3yAvg * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">PCR 3Y Avg</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.assetQualityPillar.pcr3yAvg * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item highlight">
                                  <span class="label">Asset Quality Mult</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.assetQualityPillar.assetQualityMultiplier.toFixed(3) }}</span>
                                </div>
                              </div>
                              <p class="scorecard-note">GNPA, credit cost, and PCR combine into asset quality score/multiplier</p>
                            </div>

                            <!-- Capital & Funding Pillar -->
                            <div class="scorecard-section" v-if="baseParams.scorecardSnapshot.capitalFundingPillar">
                              <h5 class="scorecard-section-title">💰 Capital & Funding Pillar</h5>
                              <div class="scorecard-grid">
                                <div class="scorecard-item">
                                  <span class="label">CAR Latest</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.capitalFundingPillar.carLatest * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">Tier 1 Latest</span>
                                  <span class="value">{{ (baseParams.scorecardSnapshot.capitalFundingPillar.tier1Latest * 100).toFixed(2) }}%</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">Debt-to-Equity</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.capitalFundingPillar.debtToEquityLatest.toFixed(2) }}x</span>
                                </div>
                                <div class="scorecard-item highlight">
                                  <span class="label">Capital/Funding Mult</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.capitalFundingPillar.capitalAndFundingMultiplier.toFixed(3) }}</span>
                                </div>
                              </div>
                              <p class="scorecard-note">Capital adequacy and leverage combine into capital/funding multiplier</p>
                            </div>

                            <!-- Governance Overlay -->
                            <div class="scorecard-section" v-if="baseParams.scorecardSnapshot.governanceOverlay">
                              <h5 class="scorecard-section-title">⚖️ Governance Overlay</h5>
                              <div class="scorecard-grid">
                                <div class="scorecard-item">
                                  <span class="label">Governance Level</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.governanceOverlay.governanceLabel }}</span>
                                </div>
                                <div class="scorecard-item">
                                  <span class="label">Governance Score</span>
                                  <span class="value">{{ baseParams.scorecardSnapshot.governanceOverlay.governanceScore }}</span>
                                </div>
                                <div class="scorecard-item highlight">
                                  <span class="label">Gov Overlay (additive)</span>
                                  <span class="value">+{{ baseParams.scorecardSnapshot.governanceOverlay.govOverlay.toFixed(2) }}</span>
                                </div>
                              </div>
                              <p class="scorecard-note">Governance adds a small additive overlay to P/B</p>
                            </div>

                            <!-- Formula Summary -->
                            <div class="scorecard-section formula-box">
                              <h5 class="scorecard-section-title">📐 Valuation Formula</h5>
                              <div class="formula-text">
                                <p><strong>Justified P/B</strong> = Base P/B × Growth Mult × Asset Quality Mult × Capital/Funding Mult + Gov Overlay (capped at Max P/B)</p>
                                <p><strong>Fair Price</strong> = Justified P/B × Book Value per Share (BVPS)</p>
                              </div>
                            </div>

                            <!-- Important Note -->
                            <div class="scorecard-section note-box">
                              <p class="font-12"><strong>Important:</strong> The four sliders above are <em>scenario tweaks</em> around this base case. The base case itself is built from the broader scorecard shown here (profitability, growth, asset quality, capital/funding, governance).</p>
                              <p class="font-12 mt-10"><strong>What this is NOT:</strong> This is not a DCF with detailed year-by-year cash flows. It is a structured P/B framework linking valuation to profitability, growth, risk, and balance sheet strength.</p>
                            </div>
                          </div>
                        </div>

                        <!-- Category Insights Accordion -->
                        <div class="scorecard-accordion" v-if="currentCategoryInsight">
                          <button
                            class="accordion-toggle"
                            @click="showCategoryInsights = !showCategoryInsights"
                          >
                            <span>{{ showCategoryInsights ? '▼' : '▶' }} {{ baseParams.scorecardSnapshot.nbfcCategory }} NBFC Category Insights</span>
                          </button>

                          <div v-show="showCategoryInsights" class="accordion-content">
                            <div class="category-insights">
                              <!-- Summary Points -->
                              <div class="insight-section">
                                <h5 class="insight-title">📈 Category Summary</h5>
                                <ul class="insight-list">
                                  <li v-for="(point, idx) in currentCategoryInsight.summary" :key="idx" class="insight-item">
                                    {{ point }}
                                  </li>
                                </ul>
                              </div>

                              <!-- Dataset Examples -->
                              <div class="insight-section">
                                <h5 class="insight-title">📊 Dataset Examples</h5>
                                <ul class="insight-list examples-list">
                                  <li v-for="(example, idx) in currentCategoryInsight.datasetExamples" :key="idx" class="insight-item example-item">
                                    {{ example }}
                                  </li>
                                </ul>
                              </div>

                              <!-- Observed Ranges -->
                              <div class="insight-section">
                                <h5 class="insight-title">📏 Observed Ranges (This Dataset)</h5>
                                <div class="ranges-grid">
                                  <div class="range-item">
                                    <span class="range-label">ROE Avg:</span>
                                    <span class="range-value">{{ currentCategoryInsight.observedRangesThisDataset.roeAvgPct }}</span>
                                  </div>
                                  <div class="range-item">
                                    <span class="range-label">AUM CAGR:</span>
                                    <span class="range-value">{{ currentCategoryInsight.observedRangesThisDataset.aumCagrPct }}</span>
                                  </div>
                                  <div class="range-item">
                                    <span class="range-label">GNPA:</span>
                                    <span class="range-value">{{ currentCategoryInsight.observedRangesThisDataset.gnpaPct }}</span>
                                  </div>
                                  <div class="range-item">
                                    <span class="range-label">Debt-to-Equity:</span>
                                    <span class="range-value">{{ currentCategoryInsight.observedRangesThisDataset.debtToEquity }}</span>
                                  </div>
                                </div>
                              </div>

                              <!-- Disclaimer -->
                              <div class="insight-section disclaimer-box">
                                <h5 class="insight-title">⚠️ Important Notes</h5>
                                <ul class="insight-list">
                                  <li v-for="(note, idx) in NBFC_CATEGORY_INSIGHTS.disclaimer" :key="idx" class="insight-item disclaimer-item">
                                    {{ note }}
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="selectedStock && loading" class="loading-section">
              <div class="loading-spinner">
                <i class="pi pi-spinner pi-spin"></i>
              </div>
              <p>Loading valuation data...</p>
            </div>
            <div v-else class="empty-tab">
              <p>Select a stock to view valuation analysis</p>
            </div>
          </div>

          <!-- Technicals Tab -->
          <div v-else-if="activeTab === 'technicals'">
            <div v-if="selectedStock && techData && techData.indicators && techData.latest">
              <StockAnalysisTechIndicators
                :symbol="techData.symbol || selectedStock"
                :latest="techData.latest"
                :indicators="techData.indicators"
                :ohlcv="techData.ohlcv || []"
              />
            </div>
            <div v-else-if="selectedStock && loading" class="loading-section">
              <div class="loading-spinner">
                <i class="pi pi-spinner pi-spin"></i>
              </div>
              <p>Loading technical indicators...</p>
            </div>
            <div v-else class="empty-tab">
              <p>Select a stock to view technical analysis</p>
            </div>
          </div>

          <!-- Portfolio Impact Tab -->
          <div v-else-if="activeTab === 'portfolio'">
            <div v-if="selectedStock && valuation && selectedStockData">
              <StockAdditionPortfolioImpact
                :selectedStock="selectedStock"
                :currentPrice="valuation.params?.currentPrice ?? baseParams?.currentPrice"
                :fallbackSector="selectedStockData.sector || 'Others'"
                :fallbackCapCategory="selectedStockData.capCategory || null"
              />
            </div>
            <div v-else-if="selectedStock && loading" class="loading-section">
              <div class="loading-spinner">
                <i class="pi pi-spinner pi-spin"></i>
              </div>
              <p>Loading portfolio impact analysis...</p>
            </div>
            <div v-else class="empty-tab">
              <p>Select a stock to view portfolio impact analysis</p>
            </div>
          </div>
        </template>
      </AnalysisTabs>
    </div>

    <!-- Stock Dialog -->
    <StockDialog
      v-if="isStockDialogOpen"
      v-model:isStockDialogOpen="isStockDialogOpen"
      :stock="stockDialogData"
    />

    <!-- Error State -->
    <div v-if="error" class="error-section">
      <div class="error-content">
        <i class="pi pi-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button @click="error = ''" class="retry-btn">Dismiss</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.stock-analysis-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
}

.page-header {
  margin-bottom: 20px;
  text-align: center;
  overflow: hidden;
}

.discovery-flow {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.flow-step {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #64748b;
  white-space: nowrap;
}

.flow-step.analysis-step {
  padding: 6px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  border-color: #3b82f6;
}

.flow-step.analysis-step i {
  color: white;
}

.analysis-types {
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.8);
  margin-left: 6px;
  font-weight: 400;
}

.flow-step i {
  font-size: 0.9rem;
  color: #3b82f6;
}

.flow-arrow {
  color: #cbd5e1;
  font-size: 0.7rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 6px 0;
}

.page-subtitle {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.4;
}

.analysis-section {
  margin-top: 16px;
}

.loading-section,
.empty-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.loading-spinner {
  margin-bottom: 12px;
}

.loading-spinner i {
  font-size: 1.5rem;
  color: #3b82f6;
}

.loading-section p,
.empty-tab p {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
}

.error-section {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

.error-content {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 500px;
}

.error-content i {
  color: #dc2626;
  font-size: 1.2rem;
}

.error-content p {
  color: #dc2626;
  margin: 0;
  flex: 1;
}

.retry-btn {
  background: #dc2626;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
}

.retry-btn:hover {
  background: #b91c1c;
}

/* Valuation section styles - keeping original functionality */
.valuation-section {
  margin-top: 12px;
}

.intrinsic-grids {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 16px;
}

.intrinsic-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.sav-dial-card,
.sav-method-card,
.sav-card {
  border: none;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  margin-bottom: 12px;
}

.sav-card-title {
  margin: 0 0 10px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

.sav-dial-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.sav-dial-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.sav-dial {
  position: relative;
  width: 180px;
  height: 90px;
  border-radius: 180px 180px 0 0;
  background: linear-gradient(90deg, #ef4444, #f3f4f6, #10b981);
  overflow: hidden;
}

.sav-dial-arc--fair {
  position: absolute;
  left: 20%;
  right: 20%;
  bottom: -30%;
  top: 30%;
  border-radius: 50%;
  border: 6px solid rgba(16, 185, 129, 0.3);
}

.sav-dial-needle {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 2px;
  height: 90px;
  background: #1f2937;
  transform-origin: 50% 100%;
  transition: transform 0.3s ease;
}

.sav-dial-legend {
  display: flex;
  justify-content: space-between;
  width: 100%;
  font-size: 0.75rem;
}

.badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
}

.badge-overvalued {
  background: #fef2f2;
  color: #dc2626;
}

.badge-fair {
  background: #f0fdf4;
  color: #16a34a;
}

.badge-undervalued {
  background: #ecfdf5;
  color: #059669;
}

.sav-dial-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 12px;
  font-size: 0.8rem;
}

.sav-dial-meta .label {
  color: #6b7280;
  font-size: 0.8rem;
  margin-bottom: 4px;
}

.sav-dial-meta .value {
  font-weight: 600;
  color: #1f2937;
}

.sav-dial-meta .value.pos {
  color: #059669;
}

.sav-dial-meta .value.neg {
  color: #dc2626;
}

.mos-label {
  margin-left: 4px;
  font-weight: 400;
  color: #6b7280;
  font-size: 0.75rem;
}

.sav-method-text {
  margin: 0;
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.5;
}

.sav-stockname-bar {
  background: #eff6ff;
  padding: 4px 8px;
  border-radius: 4px;
}

.sav-stockname-value {
  font-size: 0.85rem;
  font-weight: 600;
  color: #3b82f6;
}

/* Slider styles */
.sav-slider-row {
  margin-bottom: 16px;
}

.slider-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.slider-header--stacked {
  flex-direction: column;
  gap: 3px;
}

.slider-title {
  font-weight: 600;
  font-size: 0.8rem;
  color: #1f2937;
}

.slider-value {
  color: #6b7280;
  font-size: 0.75rem;
}

.sav-slider {
  width: 100%;
  margin-bottom: 6px;
}

.slider-scale {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  color: #9ca3af;
}

.sav-base-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.base-item .label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 2px;
}

.base-item .value {
  font-weight: 600;
  font-size: 0.8rem;
  color: #1f2937;
}

/* P/B Valuation Styles */
.pb-valuation-controls {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pb-levers-note {
  background: #eff6ff;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.slider-hint {
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  margin-top: 4px;
  margin-bottom: 0;
}

.pb-outputs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}

.pb-output-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pb-output-item .label {
  font-size: 0.75rem;
  color: #6b7280;
}

.pb-output-item .value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
}

.pb-output-item .value.pos {
  color: #059669;
}

.pb-output-item .value.neg {
  color: #dc2626;
}

.pb-output-item .value.text-primary {
  color: #3b82f6;
}

.scorecard-accordion {
  margin-top: 12px;
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
}

.accordion-toggle {
  background: transparent;
  border: none;
  padding: 8px 0;
  font-size: 0.9rem;
  color: #1f2937;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  width: 100%;
  text-align: left;
}

.accordion-toggle:hover {
  color: #3b82f6;
}

.accordion-content {
  margin-top: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 6px;
}

.scorecard-section {
  margin-bottom: 16px;
}

.scorecard-section:last-child {
  margin-bottom: 0;
}

.scorecard-section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 10px 0;
}

.scorecard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.scorecard-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
}

.scorecard-item.highlight {
  border-color: #3b82f6;
  background: #eff6ff;
}

.scorecard-item .label {
  font-size: 0.75rem;
  color: #6b7280;
}

.scorecard-item .value {
  font-weight: 600;
  font-size: 0.8rem;
  color: #1f2937;
}

.scorecard-note {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 8px 0 0 0;
  font-style: italic;
}

.formula-box {
  background: #f0f9ff;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.formula-text p {
  font-size: 0.8rem;
  color: #1f2937;
  line-height: 1.5;
  margin: 0 0 8px 0;
}

.formula-text p:last-child {
  margin-bottom: 0;
}

.note-box {
  background: #fef2f2;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #fecaca;
}

.note-box p {
  font-size: 0.8rem;
  color: #991b1b;
  margin: 0;
  line-height: 1.5;
}

.note-box .mt-10 {
  margin-top: 10px;
}

.category-insights {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.insight-section {
  padding: 12px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.insight-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 10px 0;
}

.insight-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.insight-item {
  font-size: 0.8rem;
  color: #4b5563;
  line-height: 1.5;
  margin-bottom: 8px;
  padding-left: 16px;
  position: relative;
}

.insight-item:before {
  content: '•';
  position: absolute;
  left: 0;
  color: #3b82f6;
  font-weight: bold;
}

.insight-item:last-child {
  margin-bottom: 0;
}

.examples-list .example-item {
  font-size: 0.7rem;
  font-family: 'Courier New', monospace;
  background: #f9fafb;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  margin-bottom: 8px;
  line-height: 1.4;
}

.ranges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
}

.range-item {
  display: flex;
  justify-content: space-between;
  padding: 8px;
  background: #f9fafb;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.range-label {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.range-value {
  font-size: 0.75rem;
  color: #1f2937;
  font-weight: 600;
}

.disclaimer-box {
  background: #fef2f2;
  border-color: #fecaca;
}

.disclaimer-item {
  color: #991b1b;
  font-size: 0.75rem;
}

.disclaimer-item:before {
  color: #dc2626;
}

.method-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-bottom: 10px;
  background: #eff6ff;
  color: #3b82f6;
}

.font-12 {
  font-size: 0.75rem;
}

.text-gray {
  color: #6b7280;
}

.mb-10 {
  margin-bottom: 10px;
}
</style>
